<template>
  <teleport to="body">
    <transition
      enter-active-class="transition ease-out duration-300 transform"
      enter-from-class="opacity-0 translate-y-2 sm:translate-y-0 sm:scale-95"
      enter-to-class="opacity-100 translate-y-0 sm:scale-100"
      leave-active-class="transition ease-in duration-200 transform"
      leave-from-class="opacity-100 translate-y-0 sm:scale-100"
      leave-to-class="opacity-0 translate-y-2 sm:translate-y-0 sm:scale-95"
    >
      <div
        v-if="isVisible"
        :class="positionClasses"
        class="fixed z-50 p-4 pointer-events-none"
        :role="variant === 'error' ? 'alert' : 'status'"
        :aria-live="variant === 'error' ? 'assertive' : 'polite'"
      >
        <div
          class="inline-flex items-center gap-x-3 pointer-events-auto min-w-[260px] max-w-[320px] h-auto px-4 py-3 border rounded-lg shadow-dropdown"
          :class="variantClasses.container"
        >
          <!-- Icon Slot -->
          <div v-if="showIcon || hasIconSlot" class="flex-shrink-0">
            <slot name="icon"> </slot>
          </div>

          <!-- Message -->
          <p class="font-sans text-sm flex-grow">
            <slot>{{ message }}</slot>
          </p>

          <!-- Close button; error toasts stay until dismissed -->
          <button
            v-if="showClose || variant === 'error'"
            type="button"
            @click="closeToast"
            class="ml-auto -mr-1 flex-shrink-0 p-1 rounded-md focus:outline-none focus-visible:ring-2"
            :class="variantClasses.close"
            :aria-label="$t('components.modal.close')"
          >
            <mdi-close aria-hidden="true" />
          </button>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup lang="ts">
import { ref, watch, computed, onBeforeUnmount, useSlots } from "vue";
import { useFency } from "../../composables/use-fency";

export type ToastPosition =
  | "top-left"
  | "top-center"
  | "top-right"
  | "bottom-left"
  | "bottom-center"
  | "bottom-right";

export type ToastVariant = "info" | "success" | "error";

interface Props {
  modelValue: boolean; // Controls visibility via v-model:modelValue or :model-value
  message?: string;
  position?: ToastPosition;
  duration?: number; // Duration in ms, 0 or negative means persistent
  showIcon?: boolean; // Force icon display even without slot content (uses default)
  showClose?: boolean; // Show manual close button
  /**
   * "error" announces assertively, stays until dismissed and uses the danger
   * tokens; "info"/"success" announce politely and auto-dismiss.
   */
  variant?: ToastVariant;
}

const props = withDefaults(defineProps<Props>(), {
  position: "top-right",
  duration: 4000,
  message: "",
  showIcon: true, // Default to showing the icon as per example
  showClose: false, // Default to no close button, relies on duration
  variant: "info",
});

const variantClasses = computed(() => {
  switch (props.variant) {
    case "error":
      return {
        container:
          "bg-danger-bg dark:bg-danger-bg-dark border-danger-border dark:border-danger-border-dark text-danger-text dark:text-danger-text-dark",
        close:
          "text-danger-text dark:text-danger-text-dark hover:bg-danger-border/10 focus-visible:ring-danger-border",
      };
    case "success":
      return {
        container:
          "bg-status-safe dark:bg-status-safe-dark border-status-safe-border text-heading dark:text-heading-dark",
        close:
          "text-heading dark:text-heading-dark hover:bg-status-safe-border/20 focus-visible:ring-status-safe-border",
      };
    default:
      return {
        container:
          "bg-box-container-bg dark:bg-input-bg-dark border-container-border dark:border-container-border-dark text-heading dark:text-heading-dark",
        close:
          "text-content dark:text-content-dark hover:bg-secondary-bg dark:hover:bg-secondary-bg-dark focus-visible:ring-accent",
      };
  }
});

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
}>();

const fency = useFency();
const slots = useSlots();
const hasIconSlot = computed(() => !!slots.icon);

// Internal visibility state for transition control
const isVisible = ref(props.modelValue);
let timer: ReturnType<typeof setTimeout> | null = null;

const closeToast = () => {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  isVisible.value = false;
  emit("update:modelValue", false); // Sync with parent v-model
};

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue) {
      isVisible.value = true; // Show the toast

      // Fency, in the shell, shares the moment: a cheer or a worried look.
      if (props.variant === "success" || props.variant === "error") {
        fency.feel(props.variant);
      }

      // Clear previous timer if any (e.g., rapidly showing toast)
      if (timer) clearTimeout(timer);

      // Set new timer if duration is positive. Error toasts never
      // auto-dismiss: the reader decides when they are done with them.
      if (props.duration > 0 && props.variant !== "error") {
        timer = setTimeout(() => {
          closeToast();
        }, props.duration);
      }
    } else {
      // If parent sets modelValue to false, hide immediately
      closeToast();
    }
  },
);

// Positioning classes
const positionClasses = computed(() => {
  const base = ["fixed", "z-50"]; // Outer wrapper handles fixed positioning
  switch (props.position) {
    case "top-left":
      return [...base, "top-0 left-0"];
    case "top-center":
      return [...base, "top-0 left-1/2 -translate-x-1/2"];
    case "top-right":
      return [...base, "top-0 right-0"];
    case "bottom-left":
      return [...base, "bottom-0 left-0"];
    case "bottom-center":
      return [...base, "bottom-0 left-1/2 -translate-x-1/2"];
    case "bottom-right":
    default: // Default to bottom-right if needed, though prop has default
      return [...base, "bottom-0 right-0"];
  }
});

// Clear timeout on component unmount
onBeforeUnmount(() => {
  if (timer) {
    clearTimeout(timer);
  }
});
</script>
